import type { ReactNode } from 'react';
import { Check, Copy, MapPin } from 'lucide-react';
import type { StockPlaceDto } from '@eombor/contracts';
import { IconButton, cn } from '@eombor/ui';
import { useCopyToClipboard } from '../../../shared/clipboard/useCopyToClipboard.js';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import type { WarehousePlaces } from '../model/stock-places.js';

/** Miqdor `numeric(14,3)` bilan bir xil yumaloqlanadi — hisob bu yerda emas. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

export interface WarehouseStockCardProps {
  row: WarehousePlaces;
  unit: StockPlaceDto['unit'];
  /** Nusxalash bajarilgach chaqiriladi — xabarni chaqiruvchi ko'rsatadi. */
  onCopied?: () => void;
}

/**
 * BITTA OMBOR bloki — telefon va planshetda jadval o'rnini bosadi.
 *
 * Jadvalning kamchiligi ma'lumotda emas, ENIDA edi: 375px ekranda oxirgi
 * ustunlar o'ng chetdan chiqib ketardi va aynan eng kerakli son —
 * «Mavjud» — ko'rinmasdi. Karta ichidagi gorizontal siljish esa sahifani
 * vertikal aylantirmoqchi bo'lgan barmoq ostida qimirlardi.
 *
 * Shuning uchun bu yerda ustun yo'q: har bir qiymat o'z qatorida, yorlig'i
 * bilan. Hech narsa kesilmaydi va `...` qo'yilmaydi — katak kodi va
 * partiya raqami skanerlab o'qiladigan ma'lumot va yarmi ko'rinsa,
 * umuman ko'rinmagani bilan barobar.
 */
export const WarehouseStockCard = ({ row, unit, onCopied }: WarehouseStockCardProps) => {
  /* Ombor jamisi FAQAT bir nechta katak bo'lganda chiziladi: bitta katakda
     u pastdagi sonning aynan o'zini takrorlab, blokni ikki marta o'qishga
     majbur qilardi. */
  const showTotal = row.places.length > 1;

  return (
    <li className="flex flex-col gap-2.5 rounded-[var(--radius-md)] bg-surface-2 p-3.5">
      <div className="flex min-w-0 items-start gap-2">
        <MapPin size={15} aria-hidden className="mt-0.5 shrink-0 text-text-faint" />
        {/* Uzun ombor nomi (`Qashqadaryo viloyati Qarshi shahar markaziy
            ombori`) qatorga sig'masa O'RALADI — qisqartirilmaydi. */}
        <h4 className="min-w-0 flex-1 text-sm leading-snug font-semibold break-words text-text">
          {row.warehouseName}
        </h4>
      </div>

      {showTotal ? (
        <p className="tabular flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-text-muted">
          <span>
            {t('stock.colAvailable')}{' '}
            <span className="font-semibold text-text">{qtyText(row.availableQty)}</span>{' '}
            {unitText(unit)}
          </span>
          {row.reservedQty > 0 ? (
            <span>
              {t('stock.colReserved')} {qtyText(row.reservedQty)} {unitText(unit)}
            </span>
          ) : null}
        </p>
      ) : null}

      <ul className="flex flex-col gap-2">
        {row.places.map((place) => (
          <li key={`${place.batchId}:${place.cellId}`}>
            <dl
              className={cn(
                'flex flex-col gap-1 rounded-[var(--radius-sm)] bg-surface px-3 py-2.5',
                // Ko'p katakli omborda har biri alohida qog'oz bo'lib
                // ajraladi; bitta bo'lsa qo'shimcha ramka ortiqcha.
                showTotal ? undefined : 'bg-transparent px-0 py-0',
              )}
            >
              <Field label={t('stock.colCell')}>
                <Code value={place.cellCode} copyLabel={t('stock.copyCell')} onCopied={onCopied} />
              </Field>
              <Field label={t('stock.colBatch')}>
                <Code value={place.batchNo} copyLabel={t('stock.copyBatch')} onCopied={onCopied} />
              </Field>
              {/* MAVJUD — blokdagi eng muhim son, shuning uchun kattaroq
                  va to'q rangda: skladchi kartani aynan shu son uchun
                  ochadi. */}
              <Field label={t('stock.colAvailable')} strong>
                <span className="tabular text-base font-bold text-text">
                  {qtyText(place.availableQty)}
                </span>
                <span className="text-xs text-text-muted">{unitText(unit)}</span>
              </Field>
              <Field label={t('stock.colReserved')}>
                {place.reservedQty === 0 ? (
                  <span className="text-text-faint">—</span>
                ) : (
                  <>
                    <span className="tabular text-sm text-text">
                      {qtyText(place.reservedQty)}
                    </span>
                    <span className="text-xs text-text-muted">{unitText(unit)}</span>
                  </>
                )}
              </Field>
            </dl>
          </li>
        ))}
      </ul>
    </li>
  );
};

interface FieldProps {
  label: string;
  /** Qator vizual jihatdan ajralsin — «Mavjud» uchun. */
  strong?: boolean;
  children: ReactNode;
}

/**
 * Yorliq — chapda, qiymat — o'ngda.
 *
 * `min-w-0` ikkala tomonda ham bor va bu shart: flex ichida element
 * standart holda o'z mazmunidan kichrayolmaydi, natijada uzun kod
 * qatorni kengaytirib, blokdan tashqariga chiqarardi — jadvaldagi
 * gorizontal siljish aynan shundan tug'ilgan edi.
 */
const Field = ({ label, strong = false, children }: FieldProps) => (
  <div
    className={cn(
      'flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5',
      strong ? 'pt-0.5' : undefined,
    )}
  >
    <dt className="min-w-0 shrink-0 text-xs text-text-faint">{label}</dt>
    <dd className="flex min-w-0 items-baseline gap-1.5 text-right">{children}</dd>
  </div>
);

interface CodeProps {
  value: string;
  copyLabel: string;
  onCopied?: () => void;
}

/**
 * Skanerlab o'qiladigan kod: monospace va TO'LIQ.
 *
 * `break-all` — chunki kodda bo'shliq yo'q va odatiy `break-word` uni
 * yangi qatorga o'tkazolmaydi: qator kengayib ketardi.
 */
const Code = ({ value, copyLabel, onCopied }: CodeProps) => {
  const { copy, copied } = useCopyToClipboard();

  return (
    <>
      <span className="tabular min-w-0 text-sm font-medium break-all text-text">{value}</span>
      <IconButton
        icon={
          copied ? (
            <Check size={13} aria-hidden className="text-success" />
          ) : (
            <Copy size={13} aria-hidden />
          )
        }
        label={copyLabel}
        className="size-7 shrink-0 text-text-faint"
        onClick={() => {
          /* Clipboard rad etilishi mumkin (HTTPS emas, ruxsat berilmagan).
             Hook zaxira yo'lni o'zi sinaydi va baribir uddalay olmasa
             `false` qaytaradi — o'shanda hech narsa ko'rsatilmaydi:
             "nusxalandi" degan yolg'on xabar odamni kodni qo'lda
             ko'chirmaslikka ishontirardi. */
          void copy(value).then((ok) => {
            if (ok) onCopied?.();
          });
        }}
      />
    </>
  );
};
